import { roundSymbolFor, roundSymbols } from './broker';
/**
 * Timed rounds. Each modality runs its own global clock, derived only from the
 * epoch so the API, workers and every browser agree without coordination:
 *
 *   |-- BETTING --|-- RUNNING (real market) --|-- BETTING --| ...
 *   betStart    openAt                      endAt (= next betStart)
 *
 *   Dígitos:    15s + 5s = a result every 20s: Deriv Par/Ímpar (DIGITEVEN/DIGITODD) of 1 tick
 *               whose exit tick is the one at endAt.
 *   Sobe/Desce: 15s + 15s = a result every 30s (on :00 and :30): Deriv CALL/PUT that enter on the
 *               tick at openAt and expire at endAt (date_expiry).
 *
 * Round ids are per clock: the same id means different times in each modality.
 * Entries are accepted during BETTING (minus a short CLOSING margin used to
 * send the orders). Results come from the ticks of each clock's asset (a 1s index: one tick
 * per second, see broker.ts):
 *   - Sobe/Desce: tick at openAt (Deriv's entry spot) vs tick at endAt (exit spot).
 *   - Dígitos: last digit of the tick at endAt (the 1-tick contract's exit spot).
 */

export type RoundKind = 'RISE_FALL' | 'EVEN_ODD';
export const ROUND_KINDS: readonly RoundKind[] = ['EVEN_ODD', 'RISE_FALL'];

export interface RoundClockSpec {
  betSeconds: number;
  runSeconds: number;
  cycleSeconds: number;
  /** Entries close this long before the round opens; orders are sent in this gap. */
  closingMs: number;
}

export const ROUND_CLOCKS: Record<RoundKind, RoundClockSpec> = {
  // Dígitos: a result every 20s — 13s of open entries, then the market runs to the closing tick.
  EVEN_ODD: { betSeconds: 15, runSeconds: 5, cycleSeconds: 20, closingMs: 2_000 },
  // Sobe/Desce: entries close 2.5s before the round opens; they are bought in the second before it.
  RISE_FALL: { betSeconds: 15, runSeconds: 15, cycleSeconds: 30, closingMs: 2_500 },
};

/** Clock a modality trades on: Sobe/Desce's, or the digits clock (every digit modality). */
export function roundKind(modality: string): RoundKind {
  return modality === 'RISE_FALL' ? 'RISE_FALL' : 'EVEN_ODD';
}

export type RoundPhase = 'BETTING' | 'CLOSING' | 'RUNNING';

export interface RoundInfo {
  kind: RoundKind;
  /** Round number on this kind's clock. */
  id: number;
  /** Epoch seconds. */
  betStart: number;
  openAt: number;
  endAt: number;
  phase: RoundPhase;
  /** Epoch ms when the current phase ends. */
  phaseEndsAtMs: number;
}

export type RoundWindow = Pick<RoundInfo, 'kind' | 'id' | 'betStart' | 'openAt' | 'endAt'>;

export function roundById(kind: RoundKind, id: number): RoundWindow {
  const c = ROUND_CLOCKS[kind];
  const betStart = id * c.cycleSeconds;
  return { kind, id, betStart, openAt: betStart + c.betSeconds, endAt: betStart + c.cycleSeconds };
}

/** Id of the round whose cycle contains epoch second `epoch`. */
export function roundIdAt(kind: RoundKind, epoch: number): number {
  return Math.floor(epoch / ROUND_CLOCKS[kind].cycleSeconds);
}

/** The round whose cycle contains `nowMs`, and its phase. */
export function roundAt(kind: RoundKind, nowMs: number): RoundInfo {
  const r = roundById(kind, roundIdAt(kind, nowMs / 1000));
  const closeMs = r.openAt * 1000 - ROUND_CLOCKS[kind].closingMs;
  if (nowMs < closeMs) return { ...r, phase: 'BETTING', phaseEndsAtMs: closeMs };
  if (nowMs < r.openAt * 1000) return { ...r, phase: 'CLOSING', phaseEndsAtMs: r.openAt * 1000 };
  return { ...r, phase: 'RUNNING', phaseEndsAtMs: r.endAt * 1000 };
}

/** Round currently accepting entries, or null while entries are closed. */
export function bettingRound(kind: RoundKind, nowMs: number): RoundInfo | null {
  const r = roundAt(kind, nowMs);
  return r.phase === 'BETTING' ? r : null;
}

/** Round that the next entry will go into (current if betting, else the next one). */
export function upcomingRound(kind: RoundKind, nowMs: number): RoundWindow {
  const r = roundAt(kind, nowMs);
  return r.phase === 'BETTING' ? r : roundById(kind, r.id + 1);
}

/** Only the configured round assets (one tick per second from their quotes). */
export function isRoundSymbol(symbol: string): boolean {
  return roundSymbols().includes(symbol);
}

/** The asset a modality's rounds run on: Sobe e Desce or Dígitos (and the old Par/Ímpar). */
export function roundSymbolOf(modality: string): string {
  return roundSymbolFor(roundKind(modality));
}

export type RoundMove = 'UP' | 'DOWN' | 'FLAT';

/** Published once per round per symbol and clock, from real ticks. */
export interface RoundResult {
  kind: RoundKind;
  id: number;
  symbol: string;
  openAt: number;
  endAt: number;
  open: number | null;
  close: number;
  pipSize: number;
  /** Last digit of the closing tick (Dígitos: Par/Ímpar is its parity). */
  digit: number;
  /** close vs open; null when the opening tick is missing. */
  move: RoundMove | null;
  /**
   * A played round whose two sides the broker opened at different prices (see RoundReference): each
   * side's opening and what it paid. `move` is then UP or DOWN only when one side won and the other
   * lost; any other split reads as FLAT: nobody won.
   */
  sides?: RoundSides;
}

/**
 * What one side of a round paid. Deriv's Rise/Fall (CALL/PUT) wins strictly above (Sobe) or below
 * (Desce) its entry: an equal exit loses both sides. REFUND only reads entries the broker returned
 * (history of the previous broker, or a contract cancelled by Deriv).
 */
export type SideResult = 'WIN' | 'LOSS' | 'REFUND';

export interface RoundSides {
  /** Null only on a player's own reading whose entry price is not known yet (see paidOutcome). */
  rise: { open: number | null; result: SideResult };
  fall: { open: number | null; result: SideResult };
}

export function roundMove(open: number | null, close: number): RoundMove | null {
  if (open === null) return null;
  return close > open ? 'UP' : close < open ? 'DOWN' : 'FLAT';
}

/**
 * A played round's official openings: the entry spot the broker gave the round's first entry on each
 * side. On Deriv every round entry is bought in the second before the round opens, so its entry spot
 * is the tick at openAt for both sides (verified live 2026-10-27); the per-side reading stays for
 * rounds stored under the previous broker, whose two sides could open at different prices.
 */
export interface RoundReference {
  kind: RoundKind;
  id: number;
  rise: number | null;
  fall: number | null;
}

/** What a side paid with this opening and close. */
export function sideResult(direction: 'RISE' | 'FALL', open: number, close: number): SideResult {
  if (close === open) return 'LOSS';
  return (close > open) === (direction === 'RISE') ? 'WIN' : 'LOSS';
}

/**
 * Move and (when the sides opened apart) each side's result of a round read from its official
 * openings (see RoundReference). Null when neither is known.
 */
export function referenceOutcome(opens: { rise: number | null; fall: number | null }, close: number): { open: number; move: RoundMove; sides?: RoundSides } | null {
  const rise = opens.rise ?? opens.fall;
  const fall = opens.fall ?? opens.rise;
  if (rise === null || fall === null) return null;
  const r = sideResult('RISE', rise, close);
  const f = sideResult('FALL', fall, close);
  // Equal openings: the plain move (a level close is FLAT; on Deriv both sides lost it).
  const move = rise === fall ? (roundMove(rise, close) as RoundMove) : sidesMove(r, f);
  const out = { open: rise, move };
  return rise === fall ? out : { ...out, sides: { rise: { open: rise, result: r }, fall: { open: fall, result: f } } };
}

/** Move of a round from what each side paid: UP/DOWN when one side won and the other lost; FLAT otherwise. */
export function sidesMove(rise: SideResult, fall: SideResult): RoundMove {
  return rise === 'WIN' && fall === 'LOSS' ? 'UP' : rise === 'LOSS' && fall === 'WIN' ? 'DOWN' : 'FLAT';
}

/**
 * A round whose two sides don't read as one move (see RoundReference): the close fell between their
 * openings, so nobody won (one lost, the other was returned, or both lost) — or, with the openings the
 * other way round, both won.
 */
export function isSplitRound(r: { move: RoundMove | null; sides?: { rise: { result: SideResult }; fall: { result: SideResult } } }): boolean {
  return r.move === 'FLAT' && !!r.sides && !(r.sides.rise.result === 'REFUND' && r.sides.fall.result === 'REFUND');
}

/** What a side paid, from the profit the broker reported for an entry. */
export function paidResult(profit: string | number | null): SideResult {
  const p = Number(profit ?? 0);
  return p > 0 ? 'WIN' : p < 0 ? 'LOSS' : 'REFUND';
}

/** A player's settled entry in a round: its side, what the broker paid and the price it entered at. */
export interface PaidEntry {
  direction: string | null;
  profit: string | number | null;
  entry: number | null;
}

/**
 * A player's round read from their own money (owner's decision 2026-10-23: the screen never contradicts
 * what was paid). Every settled entry counts, on both sides:
 *   - one side played: its result is the move (Sobe won = UP; Desce won = DOWN; returned = FLAT);
 *   - both sides: UP/DOWN when one won and the other lost, else FLAT with `sides` (see isSplitRound);
 *   - `mixed`: entries of the SAME side paid differently (the broker gave them different entry prices) —
 *     no single move is true, the screen shows each entry. Null when no entry has a side.
 */
export function paidOutcome(entries: readonly PaidEntry[]): { move: RoundMove; sides?: RoundSides; mixed: boolean } | null {
  const of = (d: 'RISE' | 'FALL') => entries.filter((e) => e.direction === d);
  const rise = of('RISE');
  const fall = of('FALL');
  if (!rise.length && !fall.length) return null;
  const uniform = (l: PaidEntry[]) => (l.length && l.every((e) => paidResult(e.profit) === paidResult(l[0]!.profit)) ? paidResult(l[0]!.profit) : null);
  const r = uniform(rise);
  const f = uniform(fall);
  const mixed = (rise.length > 0 && r === null) || (fall.length > 0 && f === null);
  if (mixed) return { move: 'FLAT', mixed: true };
  if (r !== null && f !== null) {
    const move = sidesMove(r, f);
    const sides: RoundSides = { rise: { open: rise[0]!.entry, result: r }, fall: { open: fall[0]!.entry, result: f } };
    return isSplitRound({ move, sides }) ? { move, sides, mixed: false } : { move, mixed: false };
  }
  if (r !== null) return { move: r === 'WIN' ? 'UP' : r === 'LOSS' ? 'DOWN' : 'FLAT', mixed: false };
  return { move: f === 'WIN' ? 'DOWN' : f === 'LOSS' ? 'UP' : 'FLAT', mixed: false };
}

/** What the round shown to everyone says a side paid: per side when the openings differ, else from the move. */
export function shownResult(r: Pick<RoundResult, 'move' | 'sides'>, direction: 'RISE' | 'FALL'): SideResult | null {
  if (r.sides) return direction === 'RISE' ? r.sides.rise.result : r.sides.fall.result;
  if (!r.move) return null;
  if (r.move === 'FLAT') return 'LOSS';
  return (r.move === 'UP') === (direction === 'RISE') ? 'WIN' : 'LOSS';
}

/**
 * Entries one player can queue in one round (manual clicks; robots send one). Every entry of a round
 * is bought in the same second, and Deriv counts buys against 360 per minute per app user.
 */
export const MAX_ROUND_ENTRIES = 5;

/** A round re-read from its official openings (see RoundReference). */
export function withRoundReference(r: RoundResult, opens: { rise: number | null; fall: number | null }): RoundResult {
  const o = referenceOutcome(opens, r.close);
  if (!o) return r;
  const { sides: _drop, ...base } = r;
  return { ...base, ...o };
}

/** Ticks between a 1-tick digit contract's entry and exit spot (verified live 2026-10-27: 0, it exits on its entry tick). */
export const DIGIT_EXIT_OFFSET = 0;

/**
 * Second a round's entries are bought. Deriv's entry spot is the first tick strictly after the
 * purchase second (verified live 2026-10-27), so:
 *   - Sobe/Desce: bought in the second before openAt, the CALL/PUT enters on the tick at openAt and
 *     its date_expiry (endAt) makes it exit on the tick at endAt;
 *   - Dígitos: the 1-tick DIGITEVEN/DIGITODD bought in the second before endAt enters and exits on
 *     the tick at endAt, the round's closing tick.
 */
export function roundSendSecond(kind: RoundKind, id: number, digitExitOffset = DIGIT_EXIT_OFFSET): number {
  const r = roundById(kind, id);
  return kind === 'EVEN_ODD' ? r.endAt - 1 - digitExitOffset : r.openAt - 1;
}
